import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ToolContext } from "@/components/tools/ToolContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Copy, CheckCircle, Tag, Globe } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type Language = 'es' | 'en';

interface MetaTags {
  title: string;
  description: string;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  twitterTitle: string;
  twitterDescription: string;
}

const MetaTagsGeneratorPage = () => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const [pageUrl, setPageUrl] = useState('');
  const [pageContent, setPageContent] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [metaTags, setMetaTags] = useState<MetaTags | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);


  const content = {
    es: {
      title: "Generador de Meta Tags IA - Luis ROC | SEO Optimizado",
      description: "Genera meta tags optimizados para SEO usando inteligencia artificial. Títulos, descripciones y meta tags perfectos para tu web.",
      pageTitle: "Generador de Meta Tags con IA",
      subtitle: "Crea meta tags perfectos para SEO usando inteligencia artificial",
      urlLabel: "URL de la página",
      urlPlaceholder: "https://tudominio.com/pagina",
      contentLabel: "Contenido de la página",
      contentPlaceholder: "Describe brevemente el contenido de tu página, productos, servicios...",
      businessLabel: "Tipo de negocio",
      businessPlaceholder: "Ej: Restaurante, Tienda online, Blog, Consultoría...",
      generateButton: "Generar Meta Tags",
      generating: "Generando...",
      results: "Meta Tags Generados",
      copyButton: "Copiar",
      copied: "Copiado",
      titleTag: "Título (Title Tag)",
      descriptionTag: "Descripción (Meta Description)",
      keywordsTag: "Palabras Clave (Keywords)",
      ogTitleTag: "Open Graph Title",
      ogDescriptionTag: "Open Graph Description",
      twitterTitleTag: "Twitter Title",
      twitterDescriptionTag: "Twitter Description",
      htmlCode: "Código HTML Completo",
      tips: "Consejos SEO",
      tip1: "El título debe tener entre 50-60 caracteres",
      tip2: "La descripción debe tener entre 150-160 caracteres",
      tip3: "Incluye tu palabra clave principal en el título",
      tip4: "Haz que la descripción sea atractiva y descriptiva"
    },
    en: {
      title: "AI Meta Tags Generator - Luis ROC | SEO Optimized",
      description: "Generate SEO-optimized meta tags using artificial intelligence. Perfect titles, descriptions and meta tags for your website.",
      pageTitle: "AI Meta Tags Generator",
      subtitle: "Create perfect SEO meta tags using artificial intelligence",
      urlLabel: "Page URL",
      urlPlaceholder: "https://yourdomain.com/page",
      contentLabel: "Page content",
      contentPlaceholder: "Briefly describe your page content, products, services...",
      businessLabel: "Business type",
      businessPlaceholder: "E.g: Restaurant, Online store, Blog, Consulting...",
      generateButton: "Generate Meta Tags",
      generating: "Generating...",
      results: "Generated Meta Tags",
      copyButton: "Copy",
      copied: "Copied",
      titleTag: "Title Tag",
      descriptionTag: "Meta Description",
      keywordsTag: "Keywords",
      ogTitleTag: "Open Graph Title",
      ogDescriptionTag: "Open Graph Description",
      twitterTitleTag: "Twitter Title",
      twitterDescriptionTag: "Twitter Description",
      htmlCode: "Complete HTML Code",
      tips: "SEO Tips",
      tip1: "Title should be 50-60 characters long",
      tip2: "Description should be 150-160 characters long",
      tip3: "Include your main keyword in the title",
      tip4: "Make the description attractive and descriptive"
    }
  };

  const currentContent = content[language];

  const handleGenerate = async () => {
    if (!pageUrl.trim() || !pageContent.trim()) return;
    
    setIsGenerating(true);
    
    // Simulate AI generation
    setTimeout(() => {
      const baseTitle = pageContent.split(' ').slice(0, 5).join(' ');
      const mockMetaTags: MetaTags = {
        title: `${baseTitle} | ${businessType || 'Negocio Online'}`,
        description: `${pageContent.substring(0, 150)}...`,
        keywords: pageContent.split(' ').slice(0, 10).join(', '),
        ogTitle: `${baseTitle} - ${businessType || 'Negocio'}`,
        ogDescription: `${pageContent.substring(0, 140)}...`,
        twitterTitle: `${baseTitle} | ${businessType || 'Negocio'}`,
        twitterDescription: `${pageContent.substring(0, 140)}...`
      };

      setMetaTags(mockMetaTags);
      setIsGenerating(false);
    }, 2000);
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const generateCompleteHTML = () => {
    if (!metaTags) return '';
    
    return `<!-- Basic Meta Tags -->
<title>${metaTags.title}</title>
<meta name="description" content="${metaTags.description}">
<meta name="keywords" content="${metaTags.keywords}">

<!-- Open Graph Meta Tags -->
<meta property="og:title" content="${metaTags.ogTitle}">
<meta property="og:description" content="${metaTags.ogDescription}">
<meta property="og:type" content="website">
<meta property="og:url" content="${pageUrl}">

<!-- Twitter Meta Tags -->
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:title" content="${metaTags.twitterTitle}">
<meta name="twitter:description" content="${metaTags.twitterDescription}">

<!-- Additional SEO Meta Tags -->
<meta name="robots" content="index, follow">
<link rel="canonical" href="${pageUrl}">`;
  };

  const meta = {
    es: {
      title: "Generador Meta Tags IA - Luis ROC | Optimización SEO",
      description: "Herramienta gratuita para generar meta tags optimizados con IA. Crea títulos, descripciones y etiquetas meta perfectas para SEO.",
      url: "https://luisroc.com/herramientas/generador-metatags"
    },
    en: {
      title: "AI Meta Tags Generator - Luis ROC | SEO Optimization",
      description: "Free tool to generate AI-optimized meta tags. Create perfect titles, descriptions and meta tags for SEO.",
      url: "https://luisroc.com/tools/meta-tags-generator"
    }
  };

  const currentMeta = meta[language];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{currentMeta.title}</title>
        <meta name="description" content={currentMeta.description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={currentMeta.title} />
        <meta property="og:description" content={currentMeta.description} />
        <meta property="og:url" content={url} />
        <meta name="keywords" content="meta tags generator, SEO meta tags, generador meta tags, optimización SEO" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2">
                <Tag className="h-8 w-8 text-primary" />
                {currentContent.pageTitle}
              </h1>
              <p className="text-xl text-muted-foreground">
                {currentContent.subtitle}
              </p>
            </div>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {currentContent.pageTitle}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {currentContent.urlLabel}
                  </label>
                  <Input
                    placeholder={currentContent.urlPlaceholder}
                    value={pageUrl}
                    onChange={(e) => setPageUrl(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {currentContent.businessLabel}
                  </label>
                  <Input
                    placeholder={currentContent.businessPlaceholder}
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {currentContent.contentLabel}
                  </label>
                  <Textarea
                    placeholder={currentContent.contentPlaceholder}
                    value={pageContent}
                    onChange={(e) => setPageContent(e.target.value)}
                    rows={4}
                  />
                </div>

                <Button 
                  onClick={handleGenerate} 
                  disabled={isGenerating || !pageUrl.trim() || !pageContent.trim()}
                  className="w-full"
                >
                  {isGenerating ? currentContent.generating : currentContent.generateButton}
                </Button>
              </CardContent>
            </Card>

            {metaTags && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5" />
                      {currentContent.results}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { key: 'title', label: currentContent.titleTag, value: metaTags.title },
                      { key: 'description', label: currentContent.descriptionTag, value: metaTags.description },
                      { key: 'keywords', label: currentContent.keywordsTag, value: metaTags.keywords },
                      { key: 'ogTitle', label: currentContent.ogTitleTag, value: metaTags.ogTitle },
                      { key: 'ogDescription', label: currentContent.ogDescriptionTag, value: metaTags.ogDescription },
                      { key: 'twitterTitle', label: currentContent.twitterTitleTag, value: metaTags.twitterTitle },
                      { key: 'twitterDescription', label: currentContent.twitterDescriptionTag, value: metaTags.twitterDescription }
                    ].map(({ key, label, value }) => (
                      <div key={key} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium">{label}</h3>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToClipboard(value, key)}
                            className="flex items-center gap-1"
                          >
                            {copiedField === key ? (
                              <>
                                <CheckCircle className="h-3 w-3" />
                                {currentContent.copied}
                              </>
                            ) : (
                              <>
                                <Copy className="h-3 w-3" />
                                {currentContent.copyButton}
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-sm bg-muted p-2 rounded font-mono break-all">
                          {value}
                        </p>
                        <div className="mt-1">
                          <Badge variant={value.length > 160 ? "destructive" : value.length > 60 ? "secondary" : "default"}>
                            {value.length} {language === 'es' ? 'caracteres' : 'characters'}
                          </Badge>
                        </div>
                      </div>
                    ))}

                    <div className="border rounded-lg p-4 mt-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium">{currentContent.htmlCode}</h3>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(generateCompleteHTML(), 'html')}
                          className="flex items-center gap-1"
                        >
                          {copiedField === 'html' ? (
                            <>
                              <CheckCircle className="h-3 w-3" />
                              {currentContent.copied}
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3" />
                              {currentContent.copyButton}
                            </>
                          )}
                        </Button>
                      </div>
                      <pre className="text-xs bg-muted p-3 rounded font-mono overflow-x-auto">
                        {generateCompleteHTML()}
                      </pre>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-blue-800">{currentContent.tips}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-blue-700">
                    <ul className="space-y-2">
                      <li>• {currentContent.tip1}</li>
                      <li>• {currentContent.tip2}</li>
                      <li>• {currentContent.tip3}</li>
                      <li>• {currentContent.tip4}</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
          <div className="max-w-4xl mx-auto">
            <ToolContext tool="meta-tags-generator" language={language} />
          </div>
        </main>
        
        <Footer language={language} />
      </div>
    </>
  );
};

export default MetaTagsGeneratorPage;